import React, { useEffect, useState, useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line } from 'recharts';
import { Activity, Users, TrendingUp, Smartphone, Monitor, Tablet, Globe, MousePointer, Trash2, MapPin, CalendarIcon, Target, CheckCircle, Download, AlertTriangle } from 'lucide-react';
import { exportEventsToCSV } from '@/utils/csvExport';
import ProductMetricsCards from '@/components/analytics/ProductMetricsCards';
import FunnelChart from '@/components/analytics/FunnelChart';
import TopPagesTable from '@/components/analytics/TopPagesTable';
import ToolUsageTable from '@/components/analytics/ToolUsageTable';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Progress } from '@/components/ui/progress';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { useToast } from '@/hooks/use-toast';
import { safeParseDate, toLocalDateStr } from '@/utils/analyticsDate';

interface AnalyticsEvent {
  id: string;
  session_id: string;
  event_type: string;
  event_data: Record<string, unknown>;
  referrer: string | null;
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  device_type: string | null;
  country: string | null;
  created_at: string;
}

interface ProcessedEvent extends AnalyticsEvent {
  parsedDate: Date;
  localDateStr: string;
  localHour: number;
}

const AnalyticsDashboard = () => {
  const [events, setEvents] = useState<AnalyticsEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(7);
  const [deleting, setDeleting] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [dailyGoal, setDailyGoal] = useState(() =>
    Number(localStorage.getItem('analytics_daily_goal')) || 100
  );
  const { toast } = useToast();

  const fetchEvents = async () => {
    setLoading(true);
    const since = new Date();
    since.setDate(since.getDate() - days);
    const sinceISO = since.toISOString();

    const PAGE_SIZE = 1000;
    const MAX_PAGES = 20;
    let allData: AnalyticsEvent[] = [];
    let page = 0;
    let truncated = false;

    while (page < MAX_PAGES) {
      const from = page * PAGE_SIZE;
      const to = from + PAGE_SIZE - 1;

      const { data, error } = await supabase
        .from('analytics_events' as any)
        .select('*')
        .gte('created_at', sinceISO)
        .order('created_at', { ascending: false })
        .order('id', { ascending: false })
        .range(from, to);

      if (error) {
        toast({ title: 'Error', description: 'No se pudieron cargar los eventos de analytics.', variant: 'destructive' });
        setEvents([]);
        setLoading(false);
        return;
      }

      const rows = (data || []) as unknown as AnalyticsEvent[];
      allData = allData.concat(rows);

      if (rows.length < PAGE_SIZE) break;
      page++;
    }

    if (page >= MAX_PAGES) {
      truncated = true;
      toast({ title: 'Aviso', description: `Se cargaron ${allData.length} eventos (máximo alcanzado). Algunos datos antiguos pueden faltar.` });
    }

    // Sort chronologically for charts
    allData.sort((a, b) => a.created_at.localeCompare(b.created_at));
    setEvents(allData);
    setLoading(false);
  };

  const handleDeleteHistory = async () => {
    setDeleting(true);
    const { error } = await supabase
      .from('analytics_events' as any)
      .delete()
      .lt('created_at', new Date().toISOString());

    if (error) {
      toast({ title: 'Error', description: 'No se pudo borrar el historial.', variant: 'destructive' });
    } else {
      toast({ title: 'Historial borrado', description: 'Todos los eventos han sido eliminados.' });
      fetchEvents();
    }
    setDeleting(false);
  };

  useEffect(() => {
    fetchEvents();
  }, [days]);

  // Pre-process all events once with robust date parsing
  const { processed, invalidCount } = useMemo(() => {
    const processed: ProcessedEvent[] = [];
    let invalidCount = 0;
    events.forEach(e => {
      const parsed = safeParseDate(e.created_at);
      if (parsed) {
        processed.push({
          ...e,
          parsedDate: parsed,
          localDateStr: toLocalDateStr(parsed),
          localHour: parsed.getHours(),
        });
      } else {
        invalidCount++;
      }
    });
    
    return { processed, invalidCount };
  }, [events]);

  // Unique sessions
  const uniqueSessions = new Set(processed.map(e => e.session_id)).size;

  // Events by type
  const eventCounts: Record<string, number> = {};
  processed.forEach(e => {
    eventCounts[e.event_type] = (eventCounts[e.event_type] || 0) + 1;
  });

  // Daily visits
  const dailyData: Record<string, { date: string; views: number; sessions: Set<string> }> = {};
  processed.forEach(e => {
    const date = e.localDateStr;
    if (!dailyData[date]) dailyData[date] = { date, views: 0, sessions: new Set() };
    if (e.event_type === 'page_view') dailyData[date].views++;
    dailyData[date].sessions.add(e.session_id);
  });
  const dailyChartData = Object.values(dailyData).map(d => ({
    date: d.date,
    views: d.views,
    sessions: d.sessions.size,
  }));

  // Device breakdown
  const deviceCounts: Record<string, number> = {};
  const deviceSessions = new Set<string>();
  processed.forEach(e => {
    if (e.device_type && !deviceSessions.has(e.session_id)) {
      deviceSessions.add(e.session_id);
      deviceCounts[e.device_type] = (deviceCounts[e.device_type] || 0) + 1;
    }
  });
  const deviceData = Object.entries(deviceCounts).map(([name, value]) => ({ name, value }));

  // Top referrers
  const referrerCounts: Record<string, number> = {};
  const referrerSessions = new Set<string>();
  processed.forEach(e => {
    if (e.referrer && !referrerSessions.has(e.session_id)) {
      referrerSessions.add(e.session_id);
      try {
        const host = new URL(e.referrer).hostname || e.referrer;
        referrerCounts[host] = (referrerCounts[host] || 0) + 1;
      } catch {
        referrerCounts[e.referrer] = (referrerCounts[e.referrer] || 0) + 1;
      }
    }
  });
  const topReferrers = Object.entries(referrerCounts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 10)
    .map(([name, value]) => ({ name, value }));

  // Countries
  const countryCounts: Record<string, number> = {};
  const countrySessions = new Set<string>();
  processed.forEach(e => {
    if (e.country && !countrySessions.has(e.session_id)) {
      countrySessions.add(e.session_id);
      countryCounts[e.country] = (countryCounts[e.country] || 0) + 1;
    }
  });
  const countryData = Object.entries(countryCounts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 15)
    .map(([name, value]) => ({ name, value }));

  // UTM sources
  const utmCounts: Record<string, number> = {};
  processed.forEach(e => {
    if (e.utm_source) {
      const key = [e.utm_source, e.utm_medium, e.utm_campaign].filter(Boolean).join(' / ');
      utmCounts[key] = (utmCounts[key] || 0) + 1;
    }
  });
  const utmData = Object.entries(utmCounts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 10)
    .map(([name, value]) => ({ name, value }));

  const DeviceIcon = ({ type }: { type: string }) => {
    if (type === 'mobile') return <Smartphone className="w-4 h-4" />;
    if (type === 'tablet') return <Tablet className="w-4 h-4" />;
    return <Monitor className="w-4 h-4" />;
  };

  if (loading) {
    return <div className="text-center py-12 text-muted-foreground">Cargando analytics...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Invalid timestamps warning */}
      {invalidCount > 0 && (
        <div className="flex items-center gap-2 text-sm text-amber-600 bg-amber-50 dark:bg-amber-950/20 rounded-lg px-4 py-2">
          <AlertTriangle className="w-4 h-4 flex-shrink-0" />
          <span>{invalidCount} evento(s) con fecha inválida fueron excluidos.</span>
        </div>
      )}

      {/* Period selector + delete button */}
      <div className="flex gap-2 items-center flex-wrap">
        {[7, 14, 30, 60, 90].map(d => (
          <button
            key={d}
            onClick={() => setDays(d)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              days === d
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted text-muted-foreground hover:bg-muted/80'
            }`}
          >
            {d} días
          </button>
        ))}
        <div className="ml-auto flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => exportEventsToCSV(events, `analytics-${days}d-${format(new Date(), 'yyyy-MM-dd')}.csv`)}
            disabled={events.length === 0}
          >
            <Download className="w-4 h-4 mr-1" />
            Exportar CSV
          </Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="destructive" size="sm" disabled={deleting}>
                <Trash2 className="w-4 h-4 mr-1" />
                Borrar historial
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>¿Borrar todo el historial?</AlertDialogTitle>
                <AlertDialogDescription>
                  Esta acción eliminará todos los eventos de analytics. No se puede deshacer.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancelar</AlertDialogCancel>
                <AlertDialogAction onClick={handleDeleteHistory}>
                  Sí, borrar todo
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <Activity className="w-8 h-8 text-purple-500" />
              <div>
                <p className="text-sm text-muted-foreground">Total eventos</p>
                <p className="text-2xl font-bold">{processed.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <Users className="w-8 h-8 text-blue-500" />
              <div>
                <p className="text-sm text-muted-foreground">Sesiones únicas</p>
                <p className="text-2xl font-bold">{uniqueSessions}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <TrendingUp className="w-8 h-8 text-green-500" />
              <div>
                <p className="text-sm text-muted-foreground">Procesamientos</p>
                <p className="text-2xl font-bold">{(eventCounts['compress'] || 0) + (eventCounts['compression_success'] || 0) + (eventCounts['tool_success'] || 0) + (eventCounts['crop_apply'] || 0) + (eventCounts['rotate_apply'] || 0) + (eventCounts['adjust_apply'] || 0) + (eventCounts['pdf_generate'] || 0) + (eventCounts['advanced_convert'] || 0)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <MousePointer className="w-8 h-8 text-orange-500" />
              <div>
                <p className="text-sm text-muted-foreground">Descargas</p>
                <p className="text-2xl font-bold">{(eventCounts['download'] || 0) + (eventCounts['download_click'] || 0) + (eventCounts['download_success'] || 0) + (eventCounts['batch_download_zip'] || 0)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Product & Performance Metrics */}
      <ProductMetricsCards eventCounts={eventCounts} events={processed} />

      {/* Daily Detail Card */}
      {(() => {
        const selectedDateStr = toLocalDateStr(selectedDate);
        const dayEvents = processed.filter(e => e.localDateStr === selectedDateStr);
        const dayPageViews = dayEvents.filter(e => e.event_type === 'page_view').length;
        const daySessions = new Set(dayEvents.map(e => e.session_id)).size;
        const goalPct = dailyGoal > 0 ? Math.min((dayPageViews / dailyGoal) * 100, 100) : 0;
        const isGoalMet = dayPageViews >= dailyGoal;

        const hourlyData = Array.from({ length: 24 }, (_, i) => ({
          hour: `${i}h`,
          views: dayEvents.filter(e => e.event_type === 'page_view' && e.localHour === i).length,
        }));

        const dayEventCounts: Record<string, number> = {};
        dayEvents.forEach(e => {
          dayEventCounts[e.event_type] = (dayEventCounts[e.event_type] || 0) + 1;
        });

        return (
          <Card>
            <CardHeader>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <CardTitle className="flex items-center gap-2">
                  <CalendarIcon className="w-5 h-5" />
                  Detalle del Día
                </CardTitle>
                <div className="flex items-center gap-3 flex-wrap">
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className={cn("w-[200px] justify-start text-left font-normal")}>
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {format(selectedDate, "PPP", { locale: es })}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={selectedDate}
                        onSelect={(d) => d && setSelectedDate(d)}
                        disabled={(date) => date > new Date()}
                        initialFocus
                        className={cn("p-3 pointer-events-auto")}
                      />
                    </PopoverContent>
                  </Popover>
                  <div className="flex items-center gap-2">
                    <Target className="w-4 h-4 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">Meta:</span>
                    <Input
                      type="number"
                      min={1}
                      value={dailyGoal}
                      onChange={(e) => {
                        const val = Number(e.target.value) || 1;
                        setDailyGoal(val);
                        localStorage.setItem('analytics_daily_goal', String(val));
                      }}
                      className="w-20 h-8 text-sm"
                    />
                  </div>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Goal progress */}
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">
                    {dayPageViews} / {dailyGoal} visitas
                  </span>
                  {isGoalMet ? (
                    <span className="flex items-center gap-1 text-emerald-600 font-medium">
                      <CheckCircle className="w-4 h-4" />
                      ¡Meta cumplida!
                    </span>
                  ) : (
                    <span className="text-muted-foreground">{Math.round(goalPct)}%</span>
                  )}
                </div>
                <Progress
                  value={goalPct}
                  className={cn("h-3", isGoalMet ? "[&>div]:bg-emerald-500" : "[&>div]:bg-amber-500")}
                />
              </div>

              {/* Stats row */}
              <div className="grid grid-cols-3 gap-4">
                <div className="text-center">
                  <p className="text-2xl font-bold">{dayPageViews}</p>
                  <p className="text-xs text-muted-foreground">Page Views</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold">{daySessions}</p>
                  <p className="text-xs text-muted-foreground">Sesiones</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold">{dayEvents.length}</p>
                  <p className="text-xs text-muted-foreground">Eventos</p>
                </div>
              </div>

              {/* Hourly chart */}
              <div>
                <p className="text-sm font-medium mb-2 text-muted-foreground">Visitas por hora</p>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={hourlyData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="hour" tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }} stroke="hsl(var(--border))" />
                    <YAxis allowDecimals={false} tick={{ fill: 'hsl(var(--muted-foreground))' }} stroke="hsl(var(--border))" />
                    <Tooltip contentStyle={{ backgroundColor: 'hsl(var(--card))', borderColor: 'hsl(var(--border))', color: 'hsl(var(--foreground))' }} />
                    <Bar dataKey="views" fill="hsl(var(--primary))" radius={[2, 2, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* Event type breakdown */}
              {Object.keys(dayEventCounts).length > 0 && (
                <div>
                  <p className="text-sm font-medium mb-2 text-muted-foreground">Eventos del día</p>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {Object.entries(dayEventCounts)
                      .sort((a, b) => b[1] - a[1])
                      .map(([type, count]) => (
                        <div key={type} className="flex items-center justify-between bg-muted/50 rounded-md px-3 py-1.5 text-sm">
                          <span className="truncate">{type}</span>
                          <span className="font-medium ml-2">{count}</span>
                        </div>
                      ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        );
      })()}

      {/* Daily views chart */}
      <Card>
        <CardHeader>
          <CardTitle>Visitas y Sesiones por Día</CardTitle>
        </CardHeader>
        <CardContent>
          {dailyChartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={dailyChartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="date" tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }} stroke="hsl(var(--border))" />
                <YAxis tick={{ fill: 'hsl(var(--muted-foreground))' }} stroke="hsl(var(--border))" />
                <Tooltip contentStyle={{ backgroundColor: 'hsl(var(--card))', borderColor: 'hsl(var(--border))', color: 'hsl(var(--foreground))' }} />
                <Line type="monotone" dataKey="views" stroke="#8b5cf6" name="Page Views" strokeWidth={2} />
                <Line type="monotone" dataKey="sessions" stroke="#3b82f6" name="Sesiones" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-center text-muted-foreground py-8">Sin datos aún</p>
          )}
        </CardContent>
      </Card>

      {/* Funnel */}
      <FunnelChart events={processed} />

      {/* Top Pages & Tool Usage */}
      <TopPagesTable events={processed} />
      <ToolUsageTable events={processed} />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Devices */}
        <Card>
          <CardHeader>
            <CardTitle>Dispositivos</CardTitle>
          </CardHeader>
          <CardContent>
            {deviceData.length > 0 ? (
              <div className="space-y-3">
                {deviceData.map(d => (
                  <div key={d.name} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <DeviceIcon type={d.name} />
                      <span className="capitalize">{d.name}</span>
                    </div>
                    <span className="font-medium">{d.value}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-muted-foreground text-sm">Sin datos</p>
            )}
          </CardContent>
        </Card>

        {/* Top Referrers */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Globe className="w-5 h-5" />
              Origen del Tráfico
            </CardTitle>
          </CardHeader>
          <CardContent>
            {topReferrers.length > 0 ? (
              <div className="space-y-2">
                {topReferrers.map(r => (
                  <div key={r.name} className="flex items-center justify-between text-sm">
                    <span className="truncate max-w-[200px]">{r.name}</span>
                    <span className="font-medium">{r.value}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-muted-foreground text-sm">Sin referrers aún</p>
            )}
          </CardContent>
        </Card>

        {/* Countries */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MapPin className="w-5 h-5" />
              Países
            </CardTitle>
          </CardHeader>
          <CardContent>
            {countryData.length > 0 ? (
              <div className="space-y-2">
                {countryData.map(c => (
                  <div key={c.name} className="flex items-center justify-between text-sm">
                    <span>{c.name}</span>
                    <span className="font-medium">{c.value}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-muted-foreground text-sm">Sin datos de país aún</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* UTM campaigns */}
      {utmData.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Campañas UTM</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={utmData}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="name" tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }} angle={-30} textAnchor="end" height={80} stroke="hsl(var(--border))" />
                <YAxis tick={{ fill: 'hsl(var(--muted-foreground))' }} stroke="hsl(var(--border))" />
                <Tooltip contentStyle={{ backgroundColor: 'hsl(var(--card))', borderColor: 'hsl(var(--border))', color: 'hsl(var(--foreground))' }} />
                <Bar dataKey="value" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* Event types breakdown */}
      <Card>
        <CardHeader>
          <CardTitle>Eventos por Tipo</CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={Object.entries(eventCounts).map(([name, value]) => ({ name, value }))}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="name" tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }} stroke="hsl(var(--border))" />
              <YAxis tick={{ fill: 'hsl(var(--muted-foreground))' }} stroke="hsl(var(--border))" />
              <Tooltip contentStyle={{ backgroundColor: 'hsl(var(--card))', borderColor: 'hsl(var(--border))', color: 'hsl(var(--foreground))' }} />
              <Bar dataKey="value" fill="#3b82f6" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>
    </div>
  );
};

export default AnalyticsDashboard;
